import { useState } from "react";
import { CheckCircle, ArrowRight, Globe, FileCheck, MessageSquare } from "lucide-react";

const VISA_WA = "50587876501";

const VISAS = [
  {
    id: "usa",
    label: "Visa Americana",
    country: "Estados Unidos",
    badge: "EE.UU.",
    badgeColor: "bg-blue-600",
    accentColor: "text-blue-400",
    borderColor: "border-blue-500/40",
    form: "Formulario DS-160",
    formDesc: "Solicitud de Visa No Inmigrante de EE.UU.",
    description:
      "Te ayudamos a llenar correctamente el formulario DS-160 para la solicitud de visa americana, paso a paso y sin errores, para maximizar tus posibilidades de aprobación.",
    waMsg: "Hola%20Printacce%2C%20necesito%20ayuda%20con%20mi%20Visa%20Americana%20(DS-160)",
    image: "https://images.pexels.com/photos/32642491/pexels-photo-32642491.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=400&w=600",
    includes: [
      "Llenado completo del formulario DS-160",
      "Revisión de datos antes de enviar",
      "Asesoría sobre documentos requeridos",
      "Orientación para la entrevista consular",
      "Toma y edición de foto para la solicitud",
    ],
  },
  {
    id: "canada",
    label: "Visa Canadiense",
    country: "Canadá",
    badge: "Canadá",
    badgeColor: "bg-red-600",
    accentColor: "text-red-400",
    borderColor: "border-red-500/40",
    form: "Portal IRCC",
    formDesc: "Solicitud de Visa de Visitante a Canadá",
    description:
      "Te asistimos en el proceso de solicitud de visa canadiense a través del portal oficial del IRCC, asegurándonos de que toda la información esté correcta y completa.",
    waMsg: "Hola%20Printacce%2C%20necesito%20ayuda%20con%20mi%20Visa%20Canadiense",
    image: "https://images.pexels.com/photos/32642491/pexels-photo-32642491.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=400&w=600",
    includes: [
      "Registro en el portal IRCC",
      "Llenado de formularios de solicitud",
      "Revisión de documentación requerida",
      "Asesoría sobre el proceso de entrevista",
      "Toma y edición de foto para la solicitud",
    ],
  },
  {
    id: "mexico",
    label: "Visa Mexicana",
    country: "México",
    badge: "México",
    badgeColor: "bg-green-700",
    accentColor: "text-green-400",
    borderColor: "border-green-500/40",
    form: "Solicitud Consular",
    formDesc: "Solicitud de Visa para México",
    description:
      "Te orientamos en el proceso de solicitud de visa mexicana, desde el llenado correcto del formulario hasta la preparación de documentos para presentar en el consulado.",
    waMsg: "Hola%20Printacce%2C%20necesito%20ayuda%20con%20mi%20Visa%20Mexicana",
    image: "https://images.pexels.com/photos/32642491/pexels-photo-32642491.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=400&w=600",
    includes: [
      "Llenado del formulario consular",
      "Lista de documentos necesarios",
      "Asesoría sobre requisitos específicos",
      "Orientación para la cita consular",
      "Toma y edición de foto para la solicitud",
    ],
  },
  {
    id: "colombia",
    label: "Visa Colombiana",
    country: "Colombia",
    badge: "Colombia",
    badgeColor: "bg-yellow-500",
    accentColor: "text-yellow-400",
    borderColor: "border-yellow-500/40",
    form: "Portal Cancillería",
    formDesc: "Solicitud de Visa de Visitante a Colombia",
    description:
      "Te acompañamos en el proceso de solicitud de visa colombiana a través del portal de la Cancillería de Colombia, asegurándonos de presentar una solicitud correcta y completa.",
    waMsg: "Hola%20Printacce%2C%20necesito%20ayuda%20con%20mi%20Visa%20Colombiana",
    image: "https://images.pexels.com/photos/32642491/pexels-photo-32642491.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=400&w=600",
    includes: [
      "Registro en el portal de Cancillería",
      "Llenado correcto del formulario",
      "Revisión de documentación adjunta",
      "Asesoría sobre tipos de visa disponibles",
      "Toma y edición de foto para la solicitud",
    ],
  },
];

const STEPS = [
  {
    icon: MessageSquare,
    step: "01",
    title: "Agenda tu Cita",
    description: "Escríbenos por WhatsApp para coordinar tu horario. Somos flexibles para atenderte.",
  },
  {
    icon: FileCheck,
    step: "02",
    title: "Llenamos el Formulario",
    description: "Te acompañamos paso a paso en el llenado correcto del formulario requerido, sin errores.",
  },
  {
    icon: Globe,
    step: "03",
    title: "Recibe tu Asesoría",
    description: "Te explicamos el proceso completo para la entrevista y los documentos necesarios.",
  },
];

export default function VisaSection() {
  const [activeVisa, setActiveVisa] = useState("usa");
  const visa = VISAS.find((v) => v.id === activeVisa);

  return (
    <section id="visa" className="py-20 md:py-28 visa-gradient relative overflow-hidden">
      <div className="absolute top-0 left-0 w-96 h-96 bg-[#E65B78]/10 rounded-full -translate-x-1/2 -translate-y-1/2 blur-3xl" />
      <div className="absolute bottom-0 right-0 w-80 h-80 bg-[#E65B78]/8 rounded-full translate-x-1/3 translate-y-1/3 blur-3xl" />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">

        {/* Header */}
        <div className="text-center mb-10">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#E65B78] mb-3">
            Servicio Especial
          </p>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Llenado de{" "}
            <span className="text-[#E65B78]">Visas Internacionales</span>
          </h2>
          <p className="text-gray-400 text-base max-w-2xl mx-auto">
            Te asistimos profesionalmente en el llenado de formularios para visas de múltiples países.
            Selecciona el país de tu interés:
          </p>
        </div>

        {/* Visa Tabs */}
        <div
          data-testid="visa-tabs"
          className="flex flex-wrap justify-center gap-3 mb-12"
        >
          {VISAS.map((v, idx) => (
            <button
              key={v.id}
              onClick={() => setActiveVisa(v.id)}
              data-testid={`visa-tab-${v.id}`}
              className={`relative flex items-center gap-2.5 px-5 py-3 rounded-full font-bold text-sm transition-all duration-200 border ${
                activeVisa === v.id
                  ? "bg-[#E65B78] text-white border-[#E65B78] shadow-lg shadow-[#E65B78]/30"
                  : "bg-white/5 text-gray-300 border-white/10 hover:bg-white/10 hover:text-white"
              }`}
            >
              <span className={`w-2 h-2 rounded-full flex-shrink-0 ${v.badgeColor}`} />
              <span>{v.label}</span>
              {idx === 0 && (
                <span className="bg-[#E65B78]/30 text-[#E65B78] text-[10px] font-black px-1.5 py-0.5 rounded-full border border-[#E65B78]/40">
                  #1
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">

          {/* Left */}
          <div key={visa.id}>
            <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border ${visa.borderColor} mb-5`}>
              <span className={`w-2 h-2 rounded-full ${visa.badgeColor}`} />
              <span className="text-white text-xs font-bold">{visa.country}</span>
              <span className={`text-xs font-semibold ${visa.accentColor}`}>{visa.form}</span>
            </div>

            <h3 className="text-2xl sm:text-3xl font-bold text-white mb-4">
              {visa.label}
            </h3>
            <p className="text-gray-300 text-base leading-relaxed mb-7">
              {visa.description}
            </p>

            <div className="flex flex-col gap-3 mb-8">
              {visa.includes.map((item) => (
                <div key={item} className="flex items-center gap-3">
                  <CheckCircle size={17} className="text-[#E65B78] flex-shrink-0" />
                  <span className="text-gray-300 text-sm font-medium">{item}</span>
                </div>
              ))}
            </div>

            {/* Photo service highlight */}
            <div className="bg-white/8 border border-[#E65B78]/30 rounded-2xl p-4 mb-6 flex items-start gap-4">
              <div className="bg-[#E65B78]/20 rounded-xl p-2.5 flex-shrink-0">
                <svg className="w-5 h-5 text-[#E65B78]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <div>
                <p className="text-white font-bold text-sm mb-0.5">Foto para Visa — Incluida en el Servicio</p>
                <p className="text-gray-400 text-xs leading-relaxed">
                  Tomamos y editamos tu foto con los requisitos oficiales para la solicitud.
                  También puedes usarla para otros trámites o a tu criterio personal.
                </p>
              </div>
            </div>

            <a
              href={`https://wa.me/${VISA_WA}?text=${visa.waMsg}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`visa-whatsapp-btn-${visa.id}`}
              className="inline-flex items-center gap-2 bg-[#E65B78] hover:bg-[#D44B66] text-white font-bold px-7 py-3.5 rounded-full transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
              </svg>
              Solicitar Asesoría — {visa.label}
              <ArrowRight size={16} />
            </a>

            <p className="mt-4 text-gray-500 text-xs">
              * Asistencia en llenado de formularios. No representa servicio legal ni garantiza aprobación.
            </p>

            {/* WhatsApp number note */}
            <div className="mt-3 flex items-center gap-2">
              <svg className="w-4 h-4 text-[#25D366]" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
              </svg>
              <span className="text-gray-400 text-xs font-medium">Línea de visas: <span className="text-[#25D366] font-bold">+505 8787-6501</span></span>
            </div>
          </div>

          {/* Right: Image + Steps */}
          <div className="flex flex-col gap-5">
            <div className="relative rounded-3xl overflow-hidden">
              <img
                src={visa.image}
                alt={visa.label}
                className="w-full h-48 object-cover rounded-3xl"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent rounded-3xl" />
              <div className="absolute bottom-4 left-4 right-4">
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 border border-white/20">
                  <p className="text-white font-bold text-base">{visa.form}</p>
                  <p className="text-gray-300 text-sm">{visa.formDesc}</p>
                </div>
              </div>
            </div>

            {STEPS.map(({ icon: Icon, step, title, description }) => (
              <div
                key={step}
                data-testid={`visa-step-${step}`}
                className="flex items-start gap-4 bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-5 hover:border-[#E65B78]/40 transition-colors"
              >
                <div className="flex-shrink-0">
                  <div className="bg-[#E65B78]/20 rounded-xl p-2.5">
                    <Icon size={20} className="text-[#E65B78]" />
                  </div>
                </div>
                <div>
                  <span className="text-xs font-black text-[#E65B78]">PASO {step}</span>
                  <h4 className="text-white font-bold text-base mt-0.5 mb-1">{title}</h4>
                  <p className="text-gray-400 text-sm leading-relaxed">{description}</p>
                </div>
              </div>
            ))}
          </div>

        </div>
      </div>
    </section>
  );
}
